import { useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut } from "lucide-react";
import {
  SIGN_OUT_CONFIRM_TITLE,
  SIGN_OUT_KEEP_WORKING,
  signOutConfirmCopy,
  type SignOutConfirmCopy,
  type SignOutConfirmCounts,
} from "@ttc-spectree/toolbox-contract";
import { cn } from "@/lib/utils";

/**
 * The question when the queue could not be read yet: the same title and
 * buttons' safe default, with no numbers, because something may be running.
 */
export const SIGN_OUT_UNKNOWN_COPY: SignOutConfirmCopy = {
  title: SIGN_OUT_CONFIRM_TITLE,
  stops: [
    { text: "Signing out stops " },
    { text: "any briefs Claude is writing now", strong: true },
    { text: ". They go back to the front of the queue and wait until you sign in again." },
  ],
  kept: "Briefs already finished are kept. Your Claude sign-in and your copies of the code stay on this computer.",
  keepLabel: SIGN_OUT_KEEP_WORKING,
  confirmLabel: "Sign out anyway",
};

interface SignOutDialogProps {
  open: boolean;
  /** What stops, what waits and what is kept, from the queue now; "unknown" before the queue could be read. */
  counts: SignOutConfirmCounts | "unknown";
  /** True while `app.signOut()` runs: both buttons wait. */
  busy?: boolean;
  /** Why the last sign-out failed, in the app's own words. */
  error?: string | null;
  /** "Keep working", Escape or a click on the scrim: nothing changes. */
  onKeepWorking: () => void;
  /** "Sign out and stop N briefs". */
  onConfirm: () => void;
}

/**
 * "Sign out while Claude is working?" (Claude Design frame B5, UX-PM-19,
 * SpecTree feature PI-SPEC-SW-1485): an in-window dialog over a scrim that
 * leaves the title bar visible. It names what stops (the briefs Claude is
 * writing now), what waits with them, and what is kept; "Keep working" has
 * the focus and changes nothing; "Sign out and stop 2 briefs" names the cost.
 * The words come from the contract's wording sheet, so the app's native
 * message box (the menu's Sign Out) says the same.
 *
 * Opened from the account menu while something runs, or before the queue
 * could be read (then without numbers, {@link SIGN_OUT_UNKNOWN_COPY}); only
 * a queue read with nothing running signs out at once (`claude-chip.tsx`).
 */
export function SignOutDialog({ open, counts, busy = false, error = null, onKeepWorking, onConfirm }: SignOutDialogProps) {
  const keepRef = useRef<HTMLButtonElement>(null);
  const copy = counts === "unknown" ? SIGN_OUT_UNKNOWN_COPY : signOutConfirmCopy(counts);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onKeepWorking();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-x-0 bottom-0 top-[42px] z-50 bg-black/[.62]"
          data-testid="sign-out-scrim"
        />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[150px] z-50 flex w-[470px] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col gap-[14px] rounded-[14px] border border-[#27272A] bg-[#141417] px-6 pb-5 pt-[22px] text-[#FAFAFA] shadow-[0_30px_80px_rgba(0,0,0,.6)] focus:outline-none"
          data-testid="sign-out-dialog"
          onOpenAutoFocus={(event) => {
            // Keep working is the default (frame B5): Enter keeps the work going.
            event.preventDefault();
            keepRef.current?.focus();
          }}
        >
          <div className="flex items-center gap-2.5">
            <LogOut className="h-5 w-5 flex-none text-[#FBBF24]" aria-hidden="true" />
            <DialogPrimitive.Title className="font-display text-base font-semibold leading-[1.3]">{copy.title}</DialogPrimitive.Title>
          </div>
          <DialogPrimitive.Description asChild>
            <div className="flex flex-col gap-[14px]">
              <p className="text-sm leading-[1.55] text-[#D4D4D8]" data-testid="sign-out-stops">
                {copy.stops.map((part, i) =>
                  part.strong ? (
                    <b key={i} className="font-semibold text-[#FAFAFA]">
                      {part.text}
                    </b>
                  ) : (
                    <span key={i}>{part.text}</span>
                  )
                )}
              </p>
              <p className="text-[13px] leading-[1.55] text-[#A1A1AA]" data-testid="sign-out-kept">
                {copy.kept}
              </p>
            </div>
          </DialogPrimitive.Description>
          {error && (
            <p className="text-[13px] leading-[1.55] text-[#FF8A9B]" role="alert" data-testid="sign-out-error">
              {error}
            </p>
          )}
          <div className="mt-1 flex justify-end gap-2">
            <button
              ref={keepRef}
              type="button"
              disabled={busy}
              onClick={onKeepWorking}
              className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium text-[#FAFAFA] transition-colors hover:bg-[#232328] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#23A4FF] disabled:opacity-60"
              data-testid="sign-out-keep-working"
            >
              {copy.keepLabel}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onConfirm}
              className={cn(
                "inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[rgba(225,24,55,.55)] bg-[#3B0D15] px-[13px] text-[13px] font-semibold text-[#FFB3BF] transition-colors hover:bg-[#4A1019] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E11837] disabled:opacity-60"
              )}
              data-testid="sign-out-confirm"
            >
              <LogOut className="h-4 w-4 flex-none" aria-hidden="true" />
              {copy.confirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
